import React, { useCallback, useEffect, useMemo } from "react"
import types from "prop-types"
import clsx from "clsx"
import { isEqual, sortBy } from "lodash-es"
import { useSelector } from "react-redux"
import { useCustomName } from "hooks"
import { DEPARTMENT_KEY, FACILITY_KEY } from "utils/customNameHelpers"
import {
  getActiveOrMatchingFacilities,
  getDepartmentFacilityMapping,
  getFacilityDepartments,
} from "reduxSlices/facilitiesSlice"
import valueMatches from "utils/valueMatches"
import InactiveOptionsItemRenderer from "shared/selects/InactiveOptionsItemRenderer"
import { ListSelect } from "shared/selects"

const itemToOption = (list) => list.map((item) => (
  { label: item.name, value: item.id, active: item.active }
))

const LocationIsDepartmentMatchValueInput = ({
  className = "",
  isFullWidth,
  onChange,
  value = null,
}) => {
  const departmentFacilityMapping = useSelector(getDepartmentFacilityMapping, isEqual)
  const facilities = useSelector(getActiveOrMatchingFacilities(departmentFacilityMapping[value]), isEqual)
  const facilityDepartments = useSelector(getFacilityDepartments, isEqual)

  const { nameInSentence: facilityNameInSentence } = useCustomName(FACILITY_KEY)
  const { nameInSentence: departmentNameInSentence } = useCustomName(DEPARTMENT_KEY)

  const sortedFacilities = useMemo(() => sortBy(facilities, "name"), [facilities])

  const sortedDepartments = useMemo(() => {
    if (!value) return []

    const facilityId = departmentFacilityMapping[value]
    const departments = facilityDepartments[facilityId]?.filter((department) => (department.active || valueMatches(department.id, value)))
    return sortBy(departments, "name")
  }, [departmentFacilityMapping, facilityDepartments, value])

  const selectFacility = useCallback((facilityId) => {
    const prevFacilityId = departmentFacilityMapping[value]

    if (facilityId !== prevFacilityId) {
      onChange(sortBy(facilityDepartments[facilityId], "name")[0].id)
    }
  }, [departmentFacilityMapping, facilityDepartments, onChange, value])

  useEffect(() => {
    if (!value) selectFacility(sortedFacilities[0].id)
  }, [selectFacility, sortedFacilities, value])

  return (
    <>
      <ListSelect
        backspaceDelete={false}
        className={clsx(className, "mb-2")}
        isFullWidth={isFullWidth}
        itemRenderer={InactiveOptionsItemRenderer}
        onChange={selectFacility}
        options={sortedFacilities}
        optionsFromList={itemToOption}
        required
        value={departmentFacilityMapping[value]}
        label={`select ${facilityNameInSentence} before selecting ${departmentNameInSentence}`}
      />
      <ListSelect
        backspaceDelete={false}
        className={className}
        isFullWidth={isFullWidth}
        itemRenderer={InactiveOptionsItemRenderer}
        onChange={onChange}
        options={sortedDepartments}
        optionsFromList={itemToOption}
        required
        value={value}
        label={`select ${departmentNameInSentence} where question will be shown if ${departmentNameInSentence} is the answer`}
      />
    </>
  )
}

LocationIsDepartmentMatchValueInput.propTypes = {
  className: types.string,
  isFullWidth: types.bool.isRequired,
  onChange: types.func.isRequired,
  value: types.number,
}

export default LocationIsDepartmentMatchValueInput
